import React, { useEffect, useContext } from 'react'
import { LinkContainer } from 'react-router-bootstrap'
import { Table, Button, Container, Row, Col } from 'react-bootstrap'
import { useDispatch, useSelector } from 'react-redux'
import { useNavigate } from 'react-router-dom'
import Message from '../components/Message.js'
import Loader from '../components/Loader.js'
import { deleteUser, listUsers } from '../actions/adminActions.js'
import { USER_LIST_RESET } from '../constants/adminConstants.js'

import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faEdit, faTrash } from '@fortawesome/free-solid-svg-icons'
import {
  faCheckCircle,
  faTimesCircle
} from '@fortawesome/free-regular-svg-icons'
import { ThemeContext } from '../components/ThemeContext.js'

const UserListScreen = () => {
  const { theme } = useContext(ThemeContext)
  const dispatch = useDispatch()
  const navigate = useNavigate()

  const userList = useSelector((state) => state.userList)
  const { loading, error, users = [] } = userList

  const userLogin = useSelector((state) => state.userLogin)
  const { userInfo } = userLogin

  const adminDeleteUser = useSelector((state) => state.adminDeleteUser)
  const {
    loading: loadingDelete,
    error: errorDelete,
    success: successDelete
  } = adminDeleteUser

  const formatDate = (date) => {
    if (!date) return '—'

    return new Intl.DateTimeFormat('en-US', {
      month: '2-digit',
      day: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      hour12: false
    }).format(new Date(date))
  }

  const deleteHandler = async (id, name) => {
    if (window.confirm(`Delete ${name || 'this user'}?`)) {
      await dispatch(deleteUser(id))
    }
  }

  useEffect(() => {
    if (userInfo?.isAdmin) {
      dispatch(listUsers())
      return
    }

    dispatch({ type: USER_LIST_RESET })
    navigate('/login')
  }, [dispatch, navigate, userInfo, successDelete])

  return (
    <Container fluid style={{ color: theme.text }}>
      <Row className='align-items-center my-2 g-2'>
        <Col>
          <h3 className='mb-0' style={{ color: theme.text }}>
            Users
          </h3>
        </Col>
        <Col xs='auto' className='text-md-end'>
          <Button
            variant='outline-primary'
            className='p-2'
            style={{
              borderColor: theme.accent,
              color: theme.accent,
              backgroundColor: 'transparent',
              border: `1px solid ${theme.accent}`
            }}
            onClick={() => navigate('/admin/user/register')}
          >
            Create User
          </Button>
        </Col>
      </Row>

      {loadingDelete && <Loader />}
      {errorDelete && <Message variant='danger'>{errorDelete}</Message>}

      {loading ? (
        <Loader />
      ) : error ? (
        <Message variant='danger'>{error}</Message>
      ) : users.length === 0 ? (
        <Message variant='info'>No users found.</Message>
      ) : (
        <div
          style={{
            overflowX: 'auto',
            backgroundColor: theme.card,
            border: `1px solid ${theme.sidebarAccent}`,
            borderRadius: '12px'
          }}
        >
          <Table
            hover
            responsive
            className='table-sm mb-0 align-middle'
            style={{ color: theme.text, marginBottom: 0 }}
          >
            <thead style={{ backgroundColor: theme.surfaceMuted }}>
              <tr>
                <th>ID</th>
                <th>LAST LOGIN</th>
                <th>NAME</th>
                <th>EMAIL</th>
                <th>ADMIN</th>
                <th>DISABLED</th>
                <th className='text-center'>ACTIONS</th>
              </tr>
            </thead>
            <tbody>
              {users.map((user) => (
                <tr key={user._id}>
                  <td className='text-break'>{user._id}</td>
                  <td>{formatDate(user.lastLogin)}</td>
                  <td>{user.name || '—'}</td>
                  <td>
                    <a
                      href={`mailto:${user.email}`}
                      style={{ color: theme.accent, textDecoration: 'none' }}
                    >
                      {user.email}
                    </a>
                  </td>
                  <td>
                    <FontAwesomeIcon
                      icon={user.isAdmin ? faCheckCircle : faTimesCircle}
                      style={{
                        color: user.isAdmin ? theme.success : theme.danger
                      }}
                    />
                  </td>
                  <td>
                    <FontAwesomeIcon
                      icon={user.isDisabled ? faCheckCircle : faTimesCircle}
                      style={{
                        color: user.isDisabled ? theme.success : theme.danger
                      }}
                    />
                  </td>
                  <td className='text-center'>
                    <LinkContainer to={`/modifyuser/${user._id}`}>
                      <Button
                        variant='outline-primary'
                        className='btn-sm me-2'
                        style={{
                          borderColor: theme.accent,
                          color: theme.accent,
                          backgroundColor: 'transparent'
                        }}
                        aria-label={`Edit ${user.name || user.email}`}
                      >
                        <FontAwesomeIcon icon={faEdit} />
                      </Button>
                    </LinkContainer>

                    <Button
                      variant='outline-danger'
                      className='btn-sm'
                      onClick={() =>
                        deleteHandler(user._id, user.name || user.email)
                      }
                      aria-label={`Delete ${user.name || user.email}`}
                    >
                      <FontAwesomeIcon icon={faTrash} />
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </Table>
        </div>
      )}
    </Container>
  )
}

export default UserListScreen
